import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { projectStatusLabel, useI18n } from '../lib/i18n';
import {
  PROVIDER_SUGGESTIONS,
  SERVER_TYPES,
  serverTypeLabel,
  type MonitoringProjectRef,
  type ServerDetail,
  type ServerType,
} from '../lib/monitoring';
import { FormGrid, FormPage } from '../components/FormPage';
import { Badge, Checkbox, Field, Input, Select, Textarea, statusTone, useToast } from '../components/ui';

/**
 * Routes: `/monitoring/servers/new` and `/monitoring/servers/:id/edit`. The
 * projects a server runs are picked here, never on the project (operator,
 * 2026-10-20); none makes it a general server. Taking a project off leaves its
 * URLs on this server with no project, still checked — the form says how many.
 */
export function MonitoringServerFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['monitoring', 'server', id],
    queryFn: () => api.get<ServerDetail>(`/monitoring/servers/${id}`),
    enabled: editing,
  });
  const projects = useQuery({
    queryKey: ['monitoring', 'project-options'],
    queryFn: () => api.get<MonitoringProjectRef[]>('/monitoring/project-options'),
  });

  const [form, setForm] = useState({
    name: '',
    ipAddress: '',
    hostname: '',
    provider: '',
    type: 'VPS' as ServerType,
    location: '',
    notes: '',
  });
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [filter, setFilter] = useState('');

  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const s = existing.data;
    setForm({
      name: s.name,
      ipAddress: s.ipAddress ?? '',
      hostname: s.hostname ?? '',
      provider: s.provider ?? '',
      type: s.type,
      location: s.location ?? '',
      notes: s.notes ?? '',
    });
    setProjectIds(s.projects.map((p) => p.id));
  }

  // Every project that may be picked, and any already on the server that may not
  // be any more (deleted since), so the list still shows what the server has.
  const choices = useMemo(() => {
    const live = projects.data ?? [];
    const kept = (existing.data?.projects ?? []).filter((p) => !live.some((l) => l.id === p.id));
    return [...live, ...kept];
  }, [projects.data, existing.data]);

  const shown = choices.filter((p) => {
    const needle = filter.trim().toLowerCase();
    return !needle || [p.name, p.nameAr ?? '', p.code].some((text) => text.toLowerCase().includes(needle));
  });

  // The URLs on this server that lose their project when it is taken off.
  const untagged = useMemo(() => {
    const byProject = new Map<string, number>();
    for (const url of existing.data?.urls ?? []) {
      if (url.projectId && !projectIds.includes(url.projectId)) {
        byProject.set(url.projectId, (byProject.get(url.projectId) ?? 0) + 1);
      }
    }
    return [...byProject.entries()].map(([projectId, n]) => ({
      project: choices.find((p) => p.id === projectId),
      n,
    }));
  }, [existing.data, projectIds, choices]);

  const toggle = (projectId: string) =>
    setProjectIds((current) =>
      current.includes(projectId) ? current.filter((x) => x !== projectId) : [...current, projectId],
    );

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, projectIds };
      return editing
        ? api.patch<ServerDetail>(`/monitoring/servers/${id}`, body)
        : api.post<ServerDetail>('/monitoring/servers', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring'] });
      navigate(`/monitoring/servers/${saved.id ?? id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${existing.data?.name ?? ''}` : t('monitoring.servers.new')}
      backTo={editing ? `/monitoring/servers/${id}` : '/monitoring/servers'}
      backLabel={editing ? (existing.data?.name ?? t('monitoring.servers.title')) : t('monitoring.servers.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!form.name.trim()}
      loading={(editing && existing.isLoading) || projects.isLoading}
      error={existing.error ?? projects.error}
      onRetry={() => {
        void existing.refetch();
        void projects.refetch();
      }}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('common.name')} required className="sm:col-span-2">
          <Input
            value={form.name}
            maxLength={120}
            placeholder="Viganium VPS"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t('monitoring.server.ip')} hint={t('monitoring.server.ipHint')}>
          <Input
            dir="ltr"
            value={form.ipAddress}
            maxLength={64}
            placeholder="203.0.113.10"
            onChange={(e) => setForm({ ...form, ipAddress: e.target.value })}
          />
        </Field>
        <Field label={t('monitoring.server.hostname')}>
          <Input
            dir="ltr"
            value={form.hostname}
            maxLength={253}
            placeholder="vps1.viganium.net"
            onChange={(e) => setForm({ ...form, hostname: e.target.value })}
          />
        </Field>
        <Field label={t('monitoring.server.provider')}>
          <Input
            list="monitoring-providers"
            value={form.provider}
            maxLength={80}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
          />
          <datalist id="monitoring-providers">
            {PROVIDER_SUGGESTIONS.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </Field>
        <Field label={t('common.type')}>
          <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ServerType })}>
            {SERVER_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(serverTypeLabel(type))}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('monitoring.server.location')}>
          <Input
            value={form.location}
            maxLength={120}
            placeholder={t('monitoring.server.locationPlaceholder')}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea value={form.notes} maxLength={2000} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>

        <div className="mt-2 border-t hairline pt-4 sm:col-span-2">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-body-sm font-medium text-[var(--text-secondary)]">{t('monitoring.server.projects')}</p>
            <span className="text-caption text-faint">
              {projectIds.length === 0
                ? t('monitoring.server.general')
                : t('monitoring.server.selectedCount', { n: projectIds.length })}
            </span>
          </div>
          <p className="mb-3 text-caption text-faint">{t('monitoring.server.projectsHint')}</p>

          {choices.length > 8 && (
            <Input
              className="mb-2"
              placeholder={t('monitoring.server.filterProjects')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
          {choices.length === 0 ? (
            <p className="text-body-sm text-muted">{t('monitoring.server.noProjects')}</p>
          ) : (
            <ul className="max-h-72 divide-y hairline overflow-y-auto rounded-md border hairline">
              {shown.map((project) => (
                <li key={project.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <Checkbox
                    checked={projectIds.includes(project.id)}
                    onChange={() => toggle(project.id)}
                    label={
                      <span>
                        <span className="me-2 font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span>
                        {localized(project)}
                      </span>
                    }
                  />
                  {project.deleted ? (
                    <Badge tone="error">{t('monitoring.server.deletedProject')}</Badge>
                  ) : (
                    <Badge tone={statusTone(project.status)}>{projectStatusLabel(project.status, locale)}</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}

          {untagged.length > 0 && (
            <div className="mt-3 space-y-1 rounded-md border border-warning/40 bg-warning-bg px-3 py-2 dark:bg-warning/10">
              {untagged.map(({ project, n }) => (
                <p key={project?.id ?? n} className="flex items-start gap-2 text-body-sm text-warning-fg dark:text-warning-dark">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {t('monitoring.server.untagWarning', { project: project ? project.code : '—', n })}
                </p>
              ))}
            </div>
          )}
        </div>
      </FormGrid>
    </FormPage>
  );
}
